import type { components } from "@jod/api-client";
import { api as defaultApi } from "../../lib/api.ts";
import type { ApiMode } from "../../lib/env.ts";
import { apiMode } from "../../lib/env.ts";
import type { MockMenuItem, MockTable } from "../../mocks/data.ts";
import { t } from "../i18n/index.ts";
import type { CreateScreenPostBody, ScreenPost, TipTarget } from "../screen/screen.ts";

export type VenueDto = components["schemas"]["Venue"];
export type MenuDto = components["schemas"]["Menu"];
export type TableDto = components["schemas"]["Table"];
export type CreateOrderRequest = components["schemas"]["CreateOrder"];
export type OrderDto = components["schemas"]["Order"];
export type SendGiftRequest = components["schemas"]["SendGift"];
export type GiftDto = components["schemas"]["Gift"];

export type CheckInRequest = components["schemas"]["CheckInRequest"];
export type CheckInSummary = components["schemas"]["CheckInSummary"];
export type UpdateTonightMode = components["schemas"]["UpdateTonightMode"];
export type CreateContactRequest = components["schemas"]["CreateContactRequest"];
export type ContactRequestDto = components["schemas"]["ContactRequest"];
export type ContactRequestInbox = components["schemas"]["ContactRequestInbox"];
export type IncomingContactRequest = components["schemas"]["IncomingContactRequest"];
export type SentContactRequest = components["schemas"]["SentContactRequest"];
export type CreateBlockRequest = components["schemas"]["CreateBlockRequest"];
export type CreateModerationReportRequest = components["schemas"]["CreateModerationReportRequest"];
export type UserProfileDto = components["schemas"]["UserProfile"];
export type UpdateProfileRequest = components["schemas"]["UpdateProfileRequest"];
export type DeleteProfileResponse = components["schemas"]["DeleteProfileResponse"];

export interface BlockedUser {
  id: string;
  blockedUserId: string;
  displayName: string;
  reason?: string | null;
  createdAt: string;
}

/**
 * Response shapes for the list endpoints that the generated client types as a
 * bare `ApiEnvelope_Value` (untyped `data`). These mirror the documented JSON
 * the Rust handlers return so the facade can present typed results to the UI.
 */
export interface AroundTable {
  tableId: string;
  tableSessionId: string;
  code: string;
  label: string;
  partyId: string;
  partyName?: string | null;
  partySize: number;
  acceptingGifts: boolean;
  acceptingContactRequests: boolean;
  isOwnTable: boolean;
}

export interface IncomingGift {
  id: string;
  status: string;
  message?: string | null;
  itemName?: string | null;
  totalSatang?: number | null;
  currency?: string | null;
  targetType: string;
  senderPartyId: string;
  senderTableCode?: string | null;
  senderTableLabel?: string | null;
  requestedContact: boolean;
  createdAt: string;
  expiresAt?: string | null;
}

export interface MyOrderItem {
  name: string;
  quantity: number;
  unitPriceSatang: number;
  currency: string;
}

export interface MyOrder {
  id: string;
  status: string;
  totalSatang: number;
  currency: string;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
  items: MyOrderItem[];
}

type ApiErrorBody = components["schemas"]["ApiErrorBody"];
type ApiResponseMeta = components["schemas"]["ApiResponseMeta"];

type ApiEnvelope<T> = {
  success: boolean;
  data?: T | null;
  error?: ApiErrorBody | null;
  meta?: ApiResponseMeta;
};

type ApiCallResult<T = unknown> = {
  data?: T;
  error?: unknown;
  response?: { status: number };
};

export type MinimalCustomerApiClient = {
  GET: (path: string, options?: unknown) => Promise<ApiCallResult>;
  POST: (path: string, options?: unknown) => Promise<ApiCallResult>;
  PUT: (path: string, options?: unknown) => Promise<ApiCallResult>;
  DELETE: (path: string, options?: unknown) => Promise<ApiCallResult>;
};

export class BackendApiRequestError extends Error {
  readonly code = "BACKEND_API_REQUEST_FAILED";
  readonly recoverable = true;

  constructor(
    message: string,
    readonly statusCode?: string | number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "BackendApiRequestError";
  }
}

export class MockModeBackendAccessError extends Error {
  readonly code = "BACKEND_FACADE_IN_MOCK_MODE";
  readonly recoverable = true;

  constructor() {
    super(
      "Customer backend facade cannot run in explicit mock mode; use the demo data path instead.",
    );
    this.name = "MockModeBackendAccessError";
  }
}

export interface CustomerBackendApi {
  checkHealth: () => Promise<{ status: string }>;
  getVenue: (venueId: string) => Promise<VenueDto>;
  getMenu: (venueId: string) => Promise<MenuDto>;
  listTables: (venueId: string) => Promise<TableDto[]>;
  createOrder: (body: CreateOrderRequest, idempotencyKey: string) => Promise<OrderDto>;
  sendGift: (body: SendGiftRequest, idempotencyKey: string) => Promise<GiftDto>;

  // Check-in + tonight mode
  createCheckIn: (body: CheckInRequest, idempotencyKey: string) => Promise<CheckInSummary>;
  getMyCheckIn: () => Promise<CheckInSummary | null>;
  getMyLayout: () => Promise<components["schemas"]["VenueLayout"] | null>;
  updateTonightMode: (body: UpdateTonightMode) => Promise<CheckInSummary>;
  leaveCheckIn: () => Promise<CheckInSummary>;

  // Discovery
  listAroundTables: () => Promise<AroundTable[]>;

  // Gift inbox
  listIncomingGifts: (includeResponded?: boolean) => Promise<IncomingGift[]>;
  acceptGift: (giftId: string) => Promise<{ id: string; status: string }>;
  rejectGift: (giftId: string) => Promise<{ id: string; status: string }>;

  // Contact requests
  createContactRequest: (body: CreateContactRequest) => Promise<ContactRequestDto>;
  listContactRequests: () => Promise<ContactRequestInbox>;
  acceptContactRequest: (id: string) => Promise<unknown>;
  rejectContactRequest: (id: string) => Promise<unknown>;
  blockContactRequest: (id: string) => Promise<unknown>;

  // Safety
  createBlock: (body: CreateBlockRequest) => Promise<unknown>;
  listBlocks: () => Promise<BlockedUser[]>;
  unblock: (blockedUserId: string) => Promise<{ unblocked: boolean }>;
  createModerationReport: (body: CreateModerationReportRequest) => Promise<unknown>;

  // Profile (FR-CUS-025, FR-CUS-132, PRIV-032)
  getMyProfile: () => Promise<UserProfileDto>;
  updateMyProfile: (body: UpdateProfileRequest) => Promise<UserProfileDto>;
  deleteMyProfile: () => Promise<DeleteProfileResponse>;

  // Orders
  listMyOrders: () => Promise<MyOrder[]>;

  // Screen (pay-to-post)
  createScreenPost: (body: CreateScreenPostBody, idempotencyKey: string) => Promise<ScreenPost>;
  listScreenPosts: () => Promise<ScreenPost[]>;
  listTipTargets: (venueSessionId: string) => Promise<TipTarget[]>;
  requestScreenUpload: (venueSessionId: string) => Promise<{ key: string; uploadUrl: string }>;
}

export interface CustomerBackendApiOptions {
  client?: MinimalCustomerApiClient;
  mode?: ApiMode;
}

export const CUSTOMER_DEMO_VENUE_ID = "11111111-1111-1111-1111-111111111111";
export const CUSTOMER_DEMO_TABLE_ID = "22222222-2222-2222-2222-222222222222";
export const CUSTOMER_DEMO_MENU_ITEM_ID = "33333333-3333-3333-3333-333333333333";
export const CUSTOMER_DEMO_RECIPIENT_PARTY_ID = "9a9a9a9a-9a9a-9a9a-9a9a-9a9a9a9a9a9b";

/**
 * Backend facade for Customer LIFF screens.
 *
 * The facade keeps generated-client paths, envelope unwrapping, and retry-safety
 * headers in one place so UI components do not duplicate backend details.
 */
export function createCustomerBackendApi({
  client = defaultApi as unknown as MinimalCustomerApiClient,
  mode = apiMode,
}: CustomerBackendApiOptions = {}): CustomerBackendApi {
  if (mode.kind === "mock") {
    throw new MockModeBackendAccessError();
  }

  return {
    checkHealth: () => requestRaw<{ status: string }>(() => client.GET("/health"), "health"),
    getVenue: (venueId) =>
      requestEnvelope<VenueDto>(
        () => client.GET("/api/v1/venues/{id}", { params: { path: { id: venueId } } }),
        "getCustomerVenue",
      ),
    getMenu: (venueId) =>
      requestEnvelope<MenuDto>(
        () => client.GET("/api/v1/menus/{venueId}", { params: { path: { venueId } } }),
        "getCustomerMenu",
      ),
    listTables: (venueId) =>
      requestEnvelope<TableDto[]>(
        () => client.GET("/api/v1/tables", { params: { query: { venueId } } }),
        "listCustomerTables",
      ),
    createOrder: (body, idempotencyKey) =>
      requestEnvelope<OrderDto>(
        () =>
          client.POST("/api/v1/orders", {
            body,
            headers: { "Idempotency-Key": idempotencyKey },
          }),
        "createCustomerOrder",
      ),
    sendGift: (body, idempotencyKey) =>
      requestEnvelope<GiftDto>(
        () =>
          client.POST("/api/v1/gifts", {
            body,
            headers: { "Idempotency-Key": idempotencyKey },
          }),
        "sendCustomerGift",
      ),

    createCheckIn: (body, idempotencyKey) =>
      requestEnvelope<CheckInSummary>(
        () =>
          client.POST("/api/v1/check-ins", {
            body,
            headers: { "Idempotency-Key": idempotencyKey },
          }),
        "createCheckIn",
      ),
    getMyCheckIn: () =>
      requestEnvelopeNullable<CheckInSummary>(
        () => client.GET("/api/v1/check-ins/me"),
        "getMyCheckIn",
      ),
    getMyLayout: () =>
      requestEnvelopeNullable<components["schemas"]["VenueLayout"]>(
        () => client.GET("/api/v1/check-ins/me/layout"),
        "getMyVenueLayout",
      ),
    updateTonightMode: (body) =>
      requestEnvelope<CheckInSummary>(
        () => client.PUT("/api/v1/check-ins/me/mode", { body }),
        "updateTonightMode",
      ),
    leaveCheckIn: () =>
      requestEnvelope<CheckInSummary>(
        () => client.POST("/api/v1/check-ins/me/leave"),
        "leaveCheckIn",
      ),

    listAroundTables: () =>
      requestEnvelope<AroundTable[]>(() => client.GET("/api/v1/tables/around"), "listAroundTables"),

    listIncomingGifts: (includeResponded) =>
      requestEnvelope<IncomingGift[]>(
        () =>
          client.GET("/api/v1/gifts/incoming", {
            params: { query: { includeResponded: includeResponded ?? false } },
          }),
        "listIncomingGifts",
      ),
    acceptGift: (giftId) =>
      requestEnvelope<{ id: string; status: string }>(
        () => client.POST("/api/v1/gifts/{id}/accept", { params: { path: { id: giftId } } }),
        "acceptGift",
      ),
    rejectGift: (giftId) =>
      requestEnvelope<{ id: string; status: string }>(
        () => client.POST("/api/v1/gifts/{id}/reject", { params: { path: { id: giftId } } }),
        "rejectGift",
      ),

    createContactRequest: (body) =>
      requestEnvelope<ContactRequestDto>(
        () => client.POST("/api/v1/contact-requests", { body }),
        "createContactRequest",
      ),
    listContactRequests: () =>
      requestEnvelope<ContactRequestInbox>(
        () => client.GET("/api/v1/contact-requests"),
        "listContactRequests",
      ),
    acceptContactRequest: (id) =>
      requestEnvelope<unknown>(
        () =>
          client.POST("/api/v1/contact-requests/{id}/accept", {
            params: { path: { id } },
          }),
        "acceptContactRequest",
      ),
    rejectContactRequest: (id) =>
      requestEnvelope<unknown>(
        () =>
          client.POST("/api/v1/contact-requests/{id}/reject", {
            params: { path: { id } },
          }),
        "rejectContactRequest",
      ),
    blockContactRequest: (id) =>
      requestEnvelope<unknown>(
        () =>
          client.POST("/api/v1/contact-requests/{id}/block", {
            params: { path: { id } },
          }),
        "blockContactRequest",
      ),

    createBlock: (body) =>
      requestEnvelope<unknown>(() => client.POST("/api/v1/blocks", { body }), "createBlock"),
    listBlocks: () =>
      requestEnvelope<BlockedUser[]>(() => client.GET("/api/v1/blocks"), "listBlocks"),
    unblock: (blockedUserId) =>
      requestEnvelope<{ unblocked: boolean }>(
        () =>
          client.DELETE("/api/v1/blocks/{blockedUserId}", {
            params: { path: { blockedUserId } },
          }),
        "unblock",
      ),
    createModerationReport: (body) =>
      requestEnvelope<unknown>(
        () => client.POST("/api/v1/moderation/reports", { body }),
        "createModerationReport",
      ),

    getMyProfile: () =>
      requestEnvelope<UserProfileDto>(() => client.GET("/api/v1/profiles/me"), "getMyProfile"),
    updateMyProfile: (body) =>
      requestEnvelope<UserProfileDto>(
        () => client.PUT("/api/v1/profiles/me", { body }),
        "updateMyProfile",
      ),
    deleteMyProfile: () =>
      requestEnvelope<DeleteProfileResponse>(
        () => client.DELETE("/api/v1/profiles/me"),
        "deleteMyProfile",
      ),

    listMyOrders: () =>
      requestEnvelope<MyOrder[]>(() => client.GET("/api/v1/orders/me"), "listMyOrders"),

    createScreenPost: (body, idempotencyKey) =>
      requestEnvelope<ScreenPost>(
        () =>
          client.POST("/api/v1/screen/posts", {
            body,
            headers: { "idempotency-key": idempotencyKey },
          }),
        "createScreenPost",
      ),
    listScreenPosts: () =>
      requestEnvelope<ScreenPost[]>(() => client.GET("/api/v1/screen/posts"), "listScreenPosts"),
    listTipTargets: (venueSessionId) =>
      requestEnvelope<TipTarget[]>(
        () =>
          client.GET("/api/v1/screen/tip-targets", {
            params: { query: { venueSessionId } },
          }),
        "listTipTargets",
      ),
    requestScreenUpload: (venueSessionId) =>
      requestEnvelope<{ key: string; uploadUrl: string }>(
        () => client.POST("/api/v1/screen/uploads", { body: { venueSessionId } }),
        "requestScreenUpload",
      ),
  };
}

export const customerBackendApi = apiMode.kind === "mock" ? null : createCustomerBackendApi();

export function menuItemFromDto(item: components["schemas"]["MenuItem"]): MockMenuItem {
  return {
    id: item.id,
    name: item.name,
    nameTh: item.nameTh ?? item.name,
    description: item.description ?? "",
    category: menuCategoryFromDto(item.category),
    priceMinor: item.price.amountMinor,
    iconName: iconForCategory(item.category),
    tags: item.giftable ? ["giftable"] : [],
    giftable: item.giftable,
    recommended: item.giftable,
    // FR-ORDER-002 / FR-CUS-064: surface availability + alcohol instead of
    // dropping them — sold-out items must show as out_of_stock (not vanish
    // or look orderable), and alcohol items must be clearly badged.
    isAvailable: item.isAvailable,
    alcohol: item.alcohol,
  };
}

export function tableFromDto(table: TableDto): MockTable {
  return {
    id: table.id,
    code: table.code,
    zone: "Backend",
    people: table.capacity,
    vibe: table.status === "available" ? "With Friends" : "Soft Open",
    statusTh: table.status === "available" ? t("table.statusOpenOrders") : t("table.statusInUse"),
    // Customer discovery stays fail-closed: unavailable tables are hidden like private tables.
    mode: ["available", "occupied"].includes(table.status)
      ? table.status === "available"
        ? "with_friends"
        : "soft_open"
      : "private",
    emoji: "🍸",
    members: [table.label],
    bioTh: `${table.label} · ${table.capacity} seats`,
    playing: false,
  };
}

async function requestRaw<T>(call: () => Promise<ApiCallResult>, operation: string): Promise<T> {
  const result = await safeCall(call, operation);
  if (result.error || result.data == null) {
    throw new BackendApiRequestError(`${operation} failed`, result.response?.status);
  }
  return result.data as T;
}

async function requestEnvelope<T>(
  call: () => Promise<ApiCallResult>,
  operation: string,
): Promise<T> {
  const result = await safeCall(call, operation);
  if (result.error || result.data == null) {
    throw new BackendApiRequestError(`${operation} failed`, result.response?.status);
  }

  const envelope = result.data as ApiEnvelope<T>;
  if (!envelope.success || envelope.error) {
    throw new BackendApiRequestError(
      envelope.error?.message ?? `${operation} failed`,
      envelope.error?.code ?? result.response?.status,
      envelope.meta?.requestId,
    );
  }
  if (envelope.data == null) {
    throw new BackendApiRequestError(
      `${operation} returned an empty data envelope`,
      result.response?.status,
      envelope.meta?.requestId,
    );
  }
  return envelope.data;
}

/**
 * Like `requestEnvelope` but tolerates a successful envelope whose `data` is
 * explicitly `null` (e.g. `GET /check-ins/me` when the caller is not checked
 * in). Returns `null` instead of throwing on the empty-data case.
 */
async function requestEnvelopeNullable<T>(
  call: () => Promise<ApiCallResult>,
  operation: string,
): Promise<T | null> {
  const result = await safeCall(call, operation);
  if (result.error || result.data == null) {
    throw new BackendApiRequestError(`${operation} failed`, result.response?.status);
  }

  const envelope = result.data as ApiEnvelope<T>;
  if (!envelope.success || envelope.error) {
    throw new BackendApiRequestError(
      envelope.error?.message ?? `${operation} failed`,
      envelope.error?.code ?? result.response?.status,
      envelope.meta?.requestId,
    );
  }
  return envelope.data ?? null;
}

async function safeCall(
  call: () => Promise<ApiCallResult>,
  operation: string,
): Promise<ApiCallResult> {
  try {
    return await call();
  } catch (error) {
    throw new BackendApiRequestError(
      error instanceof Error
        ? `${operation} transport failed: ${error.message}`
        : `${operation} transport failed`,
    );
  }
}

function menuCategoryFromDto(
  category: components["schemas"]["MenuCategory"],
): MockMenuItem["category"] {
  switch (category) {
    case "beer":
      return "Beer";
    case "cocktails":
    case "drinks":
      return "Cocktail";
    case "food":
      return "Food";
    case "snacks":
      return "Snack";
    default:
      return "Recommended";
  }
}

function iconForCategory(category: components["schemas"]["MenuCategory"]): string {
  switch (category) {
    case "beer":
      return "IconBeer";
    case "food":
    case "snacks":
      return "IconSnack";
    default:
      return "IconCocktail";
  }
}
